import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import CloseSvg from '@/components/common/CloseSvg';
import HidePasswordSvg from '@/components/common/HidePasswordSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import ViewPasswordSvg from '@/components/common/ViewPasswordSvg';
import { Button } from '@/components/ui/button';
import { Cookie } from '@/tscapi/utils/cookie';

import { useUpdatePassword } from '../hooks/useAccount';
import { en, fr } from '../i18n';
import { FormSchema } from '../schemas/password.schema';
import { isValidContainNumber, isValidTooSmall, isValidSpecial, isValidLower, isValidUpper } from '../utils/validation';
interface SecurityQuestionProps {
    email?: string;
    showSuccess: (successType: string) => void;
    handleAccordion: (accordionId: number, newIsOpen: boolean) => void;
}
type FormInput = z.infer<typeof FormSchema>;

export const FormPassword = ({ email, showSuccess, handleAccordion }: SecurityQuestionProps) => {
    const cookie: Cookie = new Cookie();
    const mutation = useUpdatePassword(cookie.UserAccessToken?.Edp, showSuccess, handleAccordion);
    const isMutating = useIsMutating();
    const {
        handleSubmit,
        register,
        control,
        setError,
        watch,
        getFieldState,
        formState: { errors, isDirty, isValid, dirtyFields, touchedFields, isSubmitting },
    } = useForm<FormInput>({
        resolver: zodResolver(FormSchema),
        mode: 'all',
        criteriaMode: 'all',
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            retypeNewPassword: '',
        },
    });

    const { touchedFields: tf } = useFormState({
        control,
    });

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);

    const toggleShowCurrentPassword = () => setShowCurrentPassword((prevState) => !prevState);
    const toggleShowNewPassword = () => setShowNewPassword((prevState) => !prevState);
    const toggleShowRetypePassword = () => setShowRetypePassword((prevState) => !prevState);

    const inputCurrent = watch('currentPassword', '');
    const inputNew = watch('newPassword', '');
    const inputRetype = watch('retypeNewPassword', '');

    const onSubmit = (data) => {
        const payload = {
            NewPassword: data.newPassword,
            NewPasswordConfirmed: data.newPassword,
            Username: email,
            CurrentPassword: data.currentPassword,
        };
        mutation.mutate(payload);
    };

    if (mutation.isError) {
        mutation.reset();
        setTimeout(() => {
            setError(
                'currentPassword',
                { type: 'custom', message: 'Sorry! Your password was incorrect, please try again.' },
                { shouldFocus: true },
            );
        }, 100);
    }
    return (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            <div className="mb-4">
                {/* --------- current password ---------*/}
                <div className="relative z-0 mb-6 mt-3 flex flex-col justify-center">
                    <input
                        className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                        type={showCurrentPassword ? 'text' : 'password'}
                        // data-empty={inputCurrent === '' ? 'true' : 'false'}
                        data-error={getFieldState('currentPassword').error ? 'true' : 'false'}
                        maxLength={16}
                        placeholder=" "
                        autoComplete="current-password"
                        tabIndex={0}
                        {...register('currentPassword')}
                        data-qm-block="true"
                    />
                    <label
                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                        htmlFor="currentPassword"
                    >
                        Current password
                    </label>
                    <button
                        className="absolute bottom-0 right-0 z-[2]"
                        type="button"
                        onClick={toggleShowCurrentPassword}
                        tabIndex={-1}
                    >
                        {showCurrentPassword ? (
                            <ViewPasswordSvg width="20" height="19" />
                        ) : (
                            <HidePasswordSvg width="20" height="20" />
                        )}
                    </button>
                    {errors.currentPassword &&
                        errors.currentPassword.message == 'Sorry! Your password was incorrect, please try again.' && (
                            <div className="mt-1 font-semibold text-[#df0c0c]">{errors.currentPassword.message}</div>
                        )}
                </div>

                {/* --------- new password --------- */}
                <div className="relative z-0 mb-6 flex flex-col justify-center">
                    <input
                        className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                        type={showNewPassword ? 'text' : 'password'}
                        maxLength={16}
                        placeholder=" "
                        data-error={getFieldState('newPassword').error ? 'true' : 'false'}
                        // data-empty={inputNew === '' ? 'true' : 'false'}
                        tabIndex={0}
                        autoComplete="new-password"
                        {...register('newPassword')}
                        data-qm-block="true"
                    />
                    <label
                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                        htmlFor="newPassword"
                    >
                        New password
                    </label>

                    <button
                        className="absolute bottom-0 right-0 z-[2]"
                        type="button"
                        onClick={toggleShowNewPassword}
                        tabIndex={-1}
                    >
                        {showNewPassword ? (
                            <ViewPasswordSvg width="20" height="19" />
                        ) : (
                            <HidePasswordSvg width="20" height="20" />
                        )}
                    </button>
                    {errors.newPassword &&
                        errors.newPassword.message == 'New password must not be the same as current password' && (
                            <div className="mt-1 font-semibold text-[#df0c0c]">{errors.newPassword.message}</div>
                        )}
                </div>

                {(getFieldState('newPassword').isTouched || getFieldState('newPassword').isDirty) && (
                    <div className="px-5 pb-7 pt-0 text-[13px]">
                        <div className="font-semibold">Your new password must have:</div>
                        <div className="flex flex-col">
                            <div
                                className={
                                    isValidTooSmall(errors)
                                        ? 'valid group pt-2 text-green-700'
                                        : 'group pt-2 text-red-500'
                                }
                            >
                                <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                <span className="pl-2.5">at least 8 characters</span>
                            </div>
                            <div
                                className={
                                    isValidUpper(errors) ? 'valid group pt-2 text-green-700' : 'group pt-2 text-red-500'
                                }
                            >
                                <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                <span className="pl-2.5">include at least 1 upper case letter</span>
                            </div>
                            <div
                                className={
                                    isValidLower(errors) ? 'valid group pt-2 text-green-700' : 'group pt-2 text-red-500'
                                }
                            >
                                <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                <span className="pl-2.5">include at least 1 lower case letter</span>
                            </div>
                            <div
                                className={
                                    isValidContainNumber(errors)
                                        ? 'valid group pt-2 text-green-700'
                                        : 'group pt-2 text-red-500'
                                }
                            >
                                <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                <span className="pl-2.5">include at least 1 number</span>
                            </div>
                            <div
                                className={
                                    isValidSpecial(errors)
                                        ? 'valid group pt-2 text-green-700'
                                        : 'group pt-2 text-red-500'
                                }
                            >
                                <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                <span className="pl-2.5">
                                    include at least 1 special character
                                    <span className="max-md:hidden md:inline">
                                        {' '}
                                        (e.g. !, @, #, $, %, ^, &, *, or ~)
                                    </span>
                                    <span className="max-md:block max-md:pl-6 md:hidden">
                                        (e.g. !, @, #, $, %, ^, &, *, or ~)
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* --------- retype password --------- */}
                <div className="relative z-0 mb-6 flex flex-col justify-center">
                    <div className="relative z-0 flex flex-col justify-center">
                        <input
                            className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            type={showRetypePassword ? 'text' : 'password'}
                            // data-empty={inputRetype === '' ? 'true' : 'false'}
                            data-error={getFieldState('retypeNewPassword').error ? 'true' : 'false'}
                            tabIndex={0}
                            placeholder=" "
                            maxLength={16}
                            autoComplete="new-password"
                            {...register('retypeNewPassword')}
                            data-qm-block="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="retypeNewPassword"
                        >
                            Retype new password
                        </label>

                        <button
                            className="absolute bottom-0 right-0 z-[2]"
                            type="button"
                            onClick={toggleShowRetypePassword}
                            tabIndex={-1}
                        >
                            {showRetypePassword ? (
                                <ViewPasswordSvg width="20" height="19" />
                            ) : (
                                <HidePasswordSvg width="20" height="20" />
                            )}
                        </button>
                    </div>
                    {errors.retypeNewPassword && (
                        <div className="mt-1 font-semibold text-[#df0c0c]">{errors.retypeNewPassword.message}</div>
                    )}
                    {mutation.isError ? (
                        <div className="mt-1 font-semibold text-[#df0c0c]">{mutation.error}</div>
                    ) : null}
                </div>
            </div>

            {/* --------- submit --------- */}
            <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                <Button
                    type="button"
                    aria-label={'Cancel'}
                    className="bg-white"
                    // className="profile__buttons cancel"
                    tabIndex={0}
                    size="lg"
                    variant="outline"
                    onClick={() => {
                        handleAccordion(0, false);
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    aria-label={'Save password'}
                    // className="profile__buttons save"
                    disabled={!isValid || isSubmitting}
                    tabIndex={!isValid ? -1 : 0}
                    size="lg"
                >
                    Save password
                </Button>
            </div>
            {mutation.isLoading || isMutating ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
        </form>
    );
};
